import { ShoppingBag } from "lucide-react";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import WishlistRow from "./WishlistRow";
import { ClockLoader } from "../loaders/ClockLoader";
import { useWishlistLogic } from "../../logic/useWishlistLogic";
import type { RootState } from "../../store/store";
import { closeWishlistModal } from "../../store/uiSlice";
import { sanitizeForUrl } from "../../utils/urlSanitizer";
import HeartWatchIcon from "../../images/heartwatch.svg";
//@ts-ignore
import "../../customCSS/Wishlist.css";
import "../../customCSS/WishlistModal.css";

/**
 * Global wishlist popup. Mounted once in App.tsx; opened via the header's
 * Wishlist icon (see layout/HeadNavBar.tsx) or the /wishlist route
 * redirector (pages/Wishlist.tsx).
 */
const WishlistModal: React.FC = () => {
  const dispatch = useDispatch();
  const open = useSelector((state: RootState) => state.ui.wishlistModalOpen);

  const handleHide = () => dispatch(closeWishlistModal());

  return (
    <Modal
      show={open}
      onHide={handleHide}
      centered
      size="lg"
      scrollable
      className="wishlist-modal"
    >
      {/* Only mounted while open, so the wishlist is fetched fresh on every
          open and never on ordinary page loads. */}
      <WishlistModalContent onHide={handleHide} />
    </Modal>
  );
};

const WishlistModalContent: React.FC<{ onHide: () => void }> = ({ onHide }) => {
  const navigate = useNavigate();
  const { wishlistProducts, loading, fetchWishlistItems } = useWishlistLogic();
  const [deletionLoading, setDeletionLoading] = useState(false);

  const goToPage = (path: string) => {
    onHide();
    navigate(path);
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>
          My Wishlist
          {!loading && (
            <span className="span-items ms-2">
              {wishlistProducts.length} {wishlistProducts.length === 1 ? "Item" : "Items"}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      {/* data-lenis-prevent: let the browser scroll this modal natively
          instead of the global Lenis smooth-scroll moving the page behind
          it (see hooks/useLenis.ts). */}
      <Modal.Body data-lenis-prevent style={{ position: "relative", minHeight: 200 }}>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <ClockLoader size={80} minuteSpeed={10} label="Unveiling your wishlist..." />
          </div>
        ) : (
          <>
            {deletionLoading && (
              <div className="wishlist-deletion-loading-overlay">
                <ClockLoader size={60} minuteSpeed={10} label="Removing..." />
              </div>
            )}

            {wishlistProducts.length === 0 ? (
              <div className="text-center py-5 animate-fade-in-up">
                <div className="mb-4">
                  <img
                    src={HeartWatchIcon}
                    alt="Empty wishlist"
                    className="wishlist-empty-heart"
                  />
                </div>
                <h3 className="mb-2 h4-mobile">Your wishlist is empty</h3>
                <p className="empty-wishlist-message mb-4 px-3">
                  Add items you love to your wishlist to keep track of them.
                </p>
                <button
                  className="btn btn-primary px-md-5 py-md-3 px-4 py-2 w-auto-mobile"
                  onClick={() => goToPage("/")}
                >
                  <ShoppingBag size={20} className="me-2" />
                  Discover Products
                </button>
              </div>
            ) : (
              <div className="wl-list">
                {wishlistProducts.map((product) => (
                  <WishlistRow
                    key={product.mappingCode}
                    product={product}
                    onOpenProduct={() =>
                      goToPage(
                        `/product/${sanitizeForUrl(product.name)}/${sanitizeForUrl(product.mappingCode)}`,
                      )
                    }
                    onChanged={async () => {
                      setDeletionLoading(true);
                      await fetchWishlistItems(true);
                      setDeletionLoading(false);
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </>
  );
};

export default WishlistModal;
